var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { ProjectSectionName } from "healthex-common/project.data.dtos";
import { DocumentVectorizationStatus } from "healthex-common/document.retrieval.dtos";
import { StudyImageSectionName, StudyPeriodicDayAndWeekUnits, StudyTranslationLanguage, } from "healthex-common/study.dtos";
import { willNeverHappen } from "healthex-common/utils";
import { computed, observable, when } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import CaliforniaBillOfRightsPDF from "../Media/california-bill-of-rights.pdf";
import { FormModel } from "./FormModel";
import { PatientConsentBaseViewModel } from "./PatientConsentBaseViewModel";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { getTranslatedStudyText } from "./TranslationService";
import { dtcOrganizationId } from "healthex-common/organization.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { getAnonymousUserId } from "../Logic/AuthManager";
const t = getObservableT("patients");
export const ConsentSignatureAnalyticsEvent = {
    CONSENT_CONFIRMED: "ConsentConfirmed",
    CONSENT_DECLINED: "ConsentDeclined",
};
export function determineOrgId(loginUIManager, studyInfo) {
    return async () => {
        var _a;
        await when(() => loginUIManager.organization !== NOT_LOADED);
        if (studyInfo !== NOT_LOADED && studyInfo !== null) {
            const studyOrgId = (_a = studyInfo.addedByOrganization) === null || _a === void 0 ? void 0 : _a.id;
            if (studyOrgId) {
                return studyOrgId;
            }
        }
        if (loginUIManager.organization !== NOT_LOADED && loginUIManager.organization !== null) {
            return loginUIManager.organization.id;
        }
        return dtcOrganizationId;
    };
}
export class PatientConsentViewModel extends PatientConsentBaseViewModel {
    constructor() {
        super(...arguments);
        this.disposer = new Disposer();
        this.guidedConsentLanguageDialog = new SimpleDialogModel();
        this.guidedConsentLanguageFormModel = new FormModel({
            language: "en",
        }, ["language"]);
        this.hipaaAuthorizationFormDialogOpen = false;
    }
    run() {
        this.disposer.reaction(() => ({
            study: this.study,
            flow: this.patientConsentFlow,
            isGuidedConsent: this.isGuidedConsent,
            isWelcomePage: this.isWelcomePage,
            studyICFTranslationAvailableLanguages: this.patientConsentUIManager.studyICFTranslationAvailableLanguages,
        }), ({ study, flow, isGuidedConsent, isWelcomePage, studyICFTranslationAvailableLanguages }) => {
            if (study !== NOT_LOADED &&
                flow !== null &&
                isGuidedConsent &&
                isWelcomePage &&
                studyICFTranslationAvailableLanguages.length > 1) {
                void this.openGuidedConsentLanguageDialog();
            }
        }, { fireImmediately: true, name: "PatientConsentViewModel_guidedConsentLanguageDialog" });
        this.disposer.reaction(() => { var _a; return (_a = this.patientConsentUIManager.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.language; }, (patientLanguage) => {
            if (patientLanguage) {
                this.guidedConsentLanguageFormModel.updateInput({
                    language: patientLanguage,
                });
            }
        }, { fireImmediately: true, name: "PatientConsentViewModel_guidedConsentLanguageFormModel" });
    }
    stop() {
        this.disposer.dispose();
    }
    async openGuidedConsentLanguageDialog() {
        var _a, _b;
        const patientLanguage = (_b = (_a = this.patientConsentUIManager.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.language) !== null && _b !== void 0 ? _b : "en";
        this.guidedConsentLanguageFormModel.updateInput({
            language: patientLanguage,
        });
        const confirmed = await this.guidedConsentLanguageDialog.showAndWait();
        if (!confirmed)
            return;
        const language = this.guidedConsentLanguageFormModel.input.language === StudyTranslationLanguage.es
            ? StudyTranslationLanguage.es
            : StudyTranslationLanguage.en;
        this.patientConsentUIManager.setInMemoryLanguage(language);
        this.closeGuidedConsentLanguageDialog();
    }
    get isPatientLoggedIn() {
        return this.patientConsentUIManager.isPatientLoggedIn;
    }
    get isDataUpdate() {
        return this.patientConsentUIManager.isDataUpdate;
    }
    get isIAS() {
        return this.patientConsentUIManager.isIAS;
    }
    get isWelcomePage() {
        const currentPage = this.patientConsentUIManager.currentPage;
        return currentPage === "ENROLLMENT_WELCOME";
    }
    get welcomeTitle() {
        if (this.isIAS) {
            return t("Get your HealthEx Unified Record");
        }
        else {
            if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
                return "";
            if (this.isDataUpdate) {
                return this.getCustomizedText("dataUpdateWelcomeTitle");
            }
            else {
                return this.getCustomizedText("welcomeTitle");
            }
        }
    }
    get supportingText() {
        if (this.isIAS) {
            return t("Access your health records from your providers in one place. HealthEx covers 80% of providers and keeps adding more.");
        }
        else {
            if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
                return "";
            if (this.isDataUpdate) {
                return this.getCustomizedText("dataUpdateWelcomeText");
            }
            else {
                return this.getCustomizedText("welcomeText");
            }
        }
    }
    closeGuidedConsentLanguageDialog() {
        this.guidedConsentLanguageDialog.open = false;
    }
    get isGuidedConsent() {
        return this.patientConsentUIManager.session.guidedConsentPatientMRN !== null;
    }
    get currentLanguage() {
        return this.patientConsentUIManager.currentLanguage;
    }
    get studyInfo() {
        return this.patientConsentUIManager.studyInfo;
    }
    get study() {
        return this.prescreenOrEnrollmentStudy;
    }
    get patientFriendlyTitle() {
        return this.patientConsentUIManager.patientFriendlyTitle;
    }
    get studySponsor() {
        return this.patientConsentUIManager.studySponsor;
    }
    get patientFriendlySummary() {
        return this.patientConsentUIManager.patientFriendlySummary;
    }
    get irbApprovalStamp() {
        return this.patientConsentUIManager.irbApprovalStamp;
    }
    get studyPrimaryContactName() {
        return this.patientConsentUIManager.studyPrimaryContactName;
    }
    get studyPrimaryContactTitle() {
        return this.patientConsentUIManager.studyPrimaryContactTitle;
    }
    get futureUseOfDataText() {
        return this.patientConsentUIManager.futureUseOfDataText;
    }
    get isProvider() {
        return this.patientConsentUIManager.isProvider;
    }
    get studyPrimaryContactDetails() {
        const details = [];
        if (this.studyPrimaryContactName) {
            details.push(this.studyPrimaryContactName);
        }
        if (this.studyPrimaryContactTitle) {
            details.push(this.studyPrimaryContactTitle);
        }
        return details.join(", ");
    }
    get patientConsentFlow() {
        return this.prescreenOrEnrollmentFlow;
    }
    get primaryContactEmail() {
        return this.patientConsentUIManager.primaryContactEmail;
    }
    get primaryContactPhone() {
        return this.patientConsentUIManager.primaryContactPhone;
    }
    get risksOfEnrollment() {
        return this.patientConsentUIManager.risksOfEnrollment;
    }
    get benefitsOfEnrollment() {
        return this.patientConsentUIManager.benefitsOfEnrollment;
    }
    get alternativesToParticipation() {
        return this.patientConsentUIManager.alternativesToParticipation;
    }
    get resourceScopes() {
        return this.patientConsentUIManager.resourceScopes;
    }
    get sensitivityScopes() {
        return this.patientConsentUIManager.sensitivityScopes;
    }
    get isFullRecordMode() {
        return this.patientConsentUIManager.isFullRecordMode;
    }
    get californiaBillOfRightsUrl() {
        if (this.isPreview)
            return null;
        return CaliforniaBillOfRightsPDF;
    }
    get isPDDEGeneralDataUse() {
        return this.patientConsentUIManager.isPDDAGeneralDataUse;
    }
    get isPDDAStudy() {
        return this.patientConsentUIManager.isPDDAStudy;
    }
    get pddaHealthRecordsUpdateFrequencyText() {
        const updateFrequency = this.patientConsentUIManager.pddaHealthRecordsUpdateFrequency;
        if (!updateFrequency)
            return null;
        switch (updateFrequency.unit) {
            case StudyPeriodicDayAndWeekUnits.DAY:
                return t("daysWithCount", { count: updateFrequency.frequency });
                break;
            case StudyPeriodicDayAndWeekUnits.WEEK:
                return t("weeksWithCount", { count: updateFrequency.frequency });
                break;
        }
        return willNeverHappen(updateFrequency.unit);
    }
    get locatingPDDERecords() {
        return this.patientConsentUIManager.locatingPDDERecords;
    }
    get canAddLocations() {
        return this.patientConsentUIManager.canAddLocations;
    }
    get zeroLocationsFound() {
        return this.patientConsentUIManager.zeroLocationsFound;
    }
    get zeroLocationsText() {
        if (!this.isPDDEGeneralDataUse)
            return null;
        if (!this.zeroLocationsFound)
            return null;
        if (this.isDataUpdate) {
            return t("At this time, no records could be retrieved.\n\nIf your records become available, we may reach out again.\n\nYou can review and change who your records are shared with from the HealthEx portal at any time.");
        }
        return t("You’ve agreed to share your records with {{ orgName }}. At this time, no records could be retrieved.\n\nIf your records become available, we may reach out again.\n\nYou can review and change who your records are shared with from the HealthEx portal at any time.", { orgName: this.organizationName });
    }
    get pddaThankYouText() {
        if (!this.isPDDAStudy)
            return "";
        const frequencyText = this.pddaHealthRecordsUpdateFrequencyText;
        if (!frequencyText) {
            return t("Your request has been successfully submitted. You will receive a confirmation message once your health information is ready to be downloaded.");
        }
        else {
            return t("Thank you for completing this request. You will be contacted again in approximately {{ frequencyText }}, based on the schedule for the study. We appreciate your participation and contribution to:", { frequencyText });
        }
    }
    get getSectionImages() {
        return (sectionName, additionalSectionId) => {
            var _a, _b, _c, _d;
            if (this.study === NOT_LOADED || this.study === null)
                return null;
            const images = (_a = this.study.studySectionImages) === null || _a === void 0 ? void 0 : _a.find((section) => section.sectionName === sectionName);
            const currentLanguage = this.patientConsentUIManager.currentLanguage;
            let studyLanguage = StudyTranslationLanguage.en;
            switch (currentLanguage) {
                case "es":
                    studyLanguage = StudyTranslationLanguage.es;
                    break;
                case "en":
                default:
                    studyLanguage = StudyTranslationLanguage.en;
                    break;
            }
            if ((sectionName === StudyImageSectionName.ADDITIONAL_SECTION || sectionName === ProjectSectionName.ADDITIONAL_INFORMATION) &&
                additionalSectionId) {
                let actualAdditionalSectionId = additionalSectionId;
                if (studyLanguage !== StudyTranslationLanguage.en) {
                    const spanishAdditionalSection = (_b = this.study.additionalSections) === null || _b === void 0 ? void 0 : _b.find((section) => section.id === additionalSectionId && section.language === studyLanguage);
                    if (spanishAdditionalSection === null || spanishAdditionalSection === void 0 ? void 0 : spanishAdditionalSection.translatedFrom) {
                        actualAdditionalSectionId = spanishAdditionalSection.translatedFrom;
                    }
                }
                let filteredImages = images === null || images === void 0 ? void 0 : images.images.filter((img) => img.additionalSectionId === actualAdditionalSectionId && img.language === studyLanguage);
                if (studyLanguage !== StudyTranslationLanguage.en && filteredImages) {
                    filteredImages = filteredImages
                        .map((img) => {
                        var _a;
                        return {
                            ...img,
                            imageUrl: img.imageUrl ||
                                (img.translatedFrom ? (_a = images === null || images === void 0 ? void 0 : images.images.find((pic) => pic.id === img.translatedFrom)) === null || _a === void 0 ? void 0 : _a.imageUrl : null) ||
                                "",
                        };
                    })
                        .filter((img) => img.imageUrl);
                }
                return (_c = filteredImages === null || filteredImages === void 0 ? void 0 : filteredImages.filter((img) => img.imageUrl)) !== null && _c !== void 0 ? _c : null;
            }
            let filteredImages = images === null || images === void 0 ? void 0 : images.images.filter((img) => !img.additionalSectionId && img.language === studyLanguage);
            if (studyLanguage !== StudyTranslationLanguage.en && filteredImages) {
                filteredImages = filteredImages
                    .map((img) => {
                    var _a;
                    return {
                        ...img,
                        imageUrl: img.imageUrl ||
                            (img.translatedFrom ? (_a = images === null || images === void 0 ? void 0 : images.images.find((pic) => pic.id === img.translatedFrom)) === null || _a === void 0 ? void 0 : _a.imageUrl : null) ||
                            "",
                    };
                })
                    .filter((img) => img.imageUrl);
            }
            return (_d = filteredImages === null || filteredImages === void 0 ? void 0 : filteredImages.filter((img) => img.imageUrl)) !== null && _d !== void 0 ? _d : null;
        };
    }
    get showFooterLanguagePickerForWelcomeScreen() {
        const shouldCheckForProjectTranslations = !this.isIAS;
        if (shouldCheckForProjectTranslations && this.patientConsentUIManager.studyICFTranslationAvailableLanguages.length <= 1) {
            return false;
        }
        return this.isPDDEGeneralDataUse && this.isWelcomePage && !this.isPreview;
    }
    get pddeGDUWelcomeConfirmButtonText() {
        if (this.isAIFlow || !this.isDataUpdate)
            return t("Get started");
        return t("Continue");
    }
    async confirmConsentOrGoToNextPage() {
        await this.patientConsentUIManager.confirmConsentOrGoToNextPage();
    }
    async confirmConsent(allowNavigationAfterConsentConfirmation) {
        await this.patientConsentUIManager.confirmConsent(allowNavigationAfterConsentConfirmation);
    }
    showPopup(route) {
        this.patientConsentUIManager.showPopup(route);
    }
    goToPage(route) {
        if (!route) {
            return;
        }
        this.patientConsentUIManager.goToPage(route);
    }
    goBack() {
        this.patientConsentUIManager.goToPreviousPage();
    }
    async declineConsent() {
        var _a, _b;
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        const identity = (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined;
        void this.analyticsService.trackHeapEvent({
            event: ConsentSignatureAnalyticsEvent.CONSENT_DECLINED,
            identity,
            user_id: userId,
            properties: { ...getHeapGlobalEventProperties() },
        });
        await this.patientConsentUIManager.declineConsent();
    }
    done() {
        if (this.isPreview)
            return;
        this.patientConsentUIManager.done();
    }
    showRequest() {
        this.patientConsentUIManager.showPopup("ENROLLMENT_REQUEST_DETAILS_POPUP");
    }
    getCustomizedText(key) {
        if (this.studyInfo !== NOT_LOADED && this.studyInfo !== null) {
            return getTranslatedStudyText(this.studyInfo, key, this.patientConsentUIManager.currentLanguage) || "";
        }
        return "";
    }
    get retrieval() {
        return this.patientConsentUIManager.retrieval;
    }
    get initialLocationsNeedingAuthentication() {
        return this.patientConsentUIManager.initialLocationsNeedingAuthentication;
    }
    get isAIFlow() {
        return this.patientConsentUIManager.isAIFlow;
    }
    get isOAuthFlow() {
        return this.patientConsentUIManager.isOAuthFlow;
    }
    get hasValidRedirectUriForAIFlow() {
        return this.patientConsentUIManager.hasValidRedirectUriForAIFlow;
    }
    get canRedirectForAIFlow() {
        return this.patientConsentUIManager.canRedirectForAIFlow;
    }
    get isVectorizationComplete() {
        var _a;
        return this.isAIFlow && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.COMPLETE;
    }
    get faqButtonText() {
        if (this.isAIFlow)
            return t("See connector FAQs");
        if (this.isIAS)
            return t("See FAQs");
        return t("See request details");
    }
}
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isPatientLoggedIn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isDataUpdate", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isIAS", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isWelcomePage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "welcomeTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "supportingText", null);
__decorate([
    observable,
    __metadata("design:type", Object)
], PatientConsentViewModel.prototype, "hipaaAuthorizationFormDialogOpen", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isGuidedConsent", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "currentLanguage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "studyInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "study", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "patientFriendlyTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "studySponsor", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "patientFriendlySummary", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "irbApprovalStamp", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "studyPrimaryContactName", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "studyPrimaryContactTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "futureUseOfDataText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isProvider", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "studyPrimaryContactDetails", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "patientConsentFlow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "primaryContactEmail", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "primaryContactPhone", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "risksOfEnrollment", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "benefitsOfEnrollment", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "alternativesToParticipation", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "resourceScopes", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "sensitivityScopes", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isFullRecordMode", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "californiaBillOfRightsUrl", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isPDDEGeneralDataUse", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isPDDAStudy", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "pddaHealthRecordsUpdateFrequencyText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "locatingPDDERecords", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "canAddLocations", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "zeroLocationsFound", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "zeroLocationsText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "pddaThankYouText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "getSectionImages", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "showFooterLanguagePickerForWelcomeScreen", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "pddeGDUWelcomeConfirmButtonText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "retrieval", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "initialLocationsNeedingAuthentication", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isAIFlow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "isVectorizationComplete", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientConsentViewModel.prototype, "faqButtonText", null);
